import { makeStyles } from '@material-ui/styles';
import { SearchOutlined } from '@ant-design/icons';
import { Table, Row, message } from 'antd';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useCallback, useMemo, useRef } from 'react';
import event from '~helpers/mixpanel';
import hexToRGBA from '~helpers/hex-to-rgba';
import { dayFormatType, formatDay } from 'common/helpers/day';
import TableSearchInput from 'common/components/select-filter/table-search-input';
import formatPrice from '~helpers/currency';
import generatePath from '~settings/routing';
import { orderStage, orderSecondaryStage } from 'common/constants/booking';
import AcceptModal from '~pages/longtail-bookings/accept-modal';
import RejectModal from '~pages/longtail-bookings/reject-modal';
import CounterOfferEditModal from '~pages/longtail-bookings/counter-offer-edit-modal';
import CounterOfferModal from '~pages/longtail-bookings/counter-offer-modal';
import { MUTATION_CONFIRM_ORDER, MUTATION_REJECT_ORDER } from '../queries';
import { setItem } from '~helpers/storage';
import AddNotesActionField from '../AddNotesActionField';
import { sortDirection, sortFields } from '~constants/bookings';
import { formatTenancyLength } from 'common/helpers/formatTenancyLength';
import { getTriggerPointOptions } from 'common/helpers/getTriggerPointOptions';
import { convertEmptyArrayToNull } from 'common/helpers/convertEmptyArrayToNull';
import { startCase } from 'lodash';
import { getStudentName } from './helpers';
import { colors } from 'common/constants/colors';
import { pageType } from '~constants/pages';
import { decodeBase64 } from 'common/helpers/decodeBase64';

const useActionFieldStyles = makeStyles(theme => ({
  row: {
    ...theme.flex.mainStart,
    ...theme.flex.crossCenter,
  },
  wrapper: {
    '& .ant-modal-header': {
      paddingLeft: 60,
      paddingRight: 60,
    },

    '& .ant-modal-body': {
      padding: '0 60px',
    },

    '& .ant-form-item-label': {
      lineHeight: '22px !important',
      color: theme.colors.titleColor,
      marginBottom: 8,
    },

    '& .optional-label': {
      color: theme.colors.captionColor,
    },
  },
  button: {
    ...theme.typography.body2,
    padding: 0,
    border: 0,
    outline: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: theme.colors.mainGreen6,
    wordBreak: 'normal',
  },
  buttonReconcile: {
    marginRight: 0,
  },
  emptyAction: {
    color: hexToRGBA('#000', 0.45),
  },
  action: {
    '& > .ant-table-header-column': {
      marginLeft: 54,
    },
  },
  actionReconcile: {
    '& > .ant-table-header-column': {
      marginLeft: 28,
    },
    textAlign: 'center',
  },
  searchColumn: {
    minWidth: 160,
  },
  dateColumn: {
    minWidth: 140,
  },
  divider: {
    height: 12,
    width: 1,
    backgroundColor: hexToRGBA('#000', 0.06),
    marginLeft: 7.5,
    marginRight: 7.5,
  },
}));

export const OrderActionField = ({
  stage,
  bookingId,
  refetchData,
  record,
  from,
  isHerculesBooking,
  openOrderCounterOfferModal,
}) => {
  const { t } = useTranslation();
  const classes = useActionFieldStyles();
  const history = useHistory();

  const [mutationConfirmOrder, { loading: confirmLoading }] = useMutation(MUTATION_CONFIRM_ORDER, {
    variables: { input: { id: bookingId } },
    onCompleted: () => {
      const { bookingJourney, referenceId, property } = record || {};
      const { name, id, slug } = property || {};
      const decodedId = decodeBase64(id)?.id;
      event('Successfully Actioned on Booking', {
        'Action Type': 'Accept',
        'Booking ID': referenceId,
        'Property Name': name,
        'Property ID': decodedId,
        'Property Slug': slug,
        'Booking Journey': bookingJourney?.toLowerCase(),
        // after confirm, stage will be changed to WAITING_FOR_PAYMENT
        'Booking Stage': orderStage.WAITING_FOR_PAYMENT.toLowerCase(),
        'Current Page Type': from,
      });

      refetchData();
    },
    onError: e => {
      message.error(e.toString());
    },
  });

  const [mutationRejectOrder, { loading: rejectLoading }] = useMutation(MUTATION_REJECT_ORDER, {
    onCompleted: () => {
      const { bookingJourney, referenceId, property } = record || {};
      const { name, id, slug } = property || {};
      const decodedId = decodeBase64(id)?.id;
      event('Successfully Actioned on Booking', {
        'Action Type': 'Reject',
        'Booking ID': referenceId,
        'Property Name': name,
        'Property ID': decodedId,
        'Property Slug': slug,
        'Booking Journey': bookingJourney?.toLowerCase(),
        // after confirm, stage will be changed to LOST
        'Booking Stage': orderStage.LOST.toLowerCase(),
        'Current Page Type': from,
      });

      refetchData();
    },
    onError: e => {
      message.error(e.toString());
    },
  });

  const handleClickCounterEventTrack = record => {
    const { property, referenceId, bookingJourney } = record || {};
    const { name, id, slug } = property || {};
    const decodedId = decodeBase64(id)?.id;
    const eventProperties = {
      'Clicked Position': 'Counter',
      'Property Name': name,
      'Property ID': decodedId,
      'Property Slug': slug,
      'Booking Stage': stage?.toLowerCase(),
      'Booking Journey': bookingJourney?.toLowerCase(),
      'Booking ID': referenceId,
      'Current Page Type': from,
    };

    if (history?.location?.pathname === '/board') {
      event('Clicked Pending Information Btn', eventProperties);
      setItem('counterOfferFrom', 'Dashboard Shortcut');
    }

    if (history?.location?.pathname === '/booking/list') {
      event('Clicked Action Btn in Booking List Page', eventProperties);
      setItem('counterOfferFrom', 'Booking List Shortcut');
    }

    return openOrderCounterOfferModal(record);
  };
  return (
    <Row
      className={classes.row}
      onClick={e => {
        e.stopPropagation();
      }}
    >
      <If condition={stage === orderStage.WAITING_FOR_CONFIRMATION}>
        <Choose>
          <When condition={record.secondaryStage === orderSecondaryStage.PENDING_COUNTER_OFFER}>
            <CounterOfferEditModal
              t={t}
              id={bookingId}
              record={record}
              confirmOrder={mutationConfirmOrder}
              rejectOrder={mutationRejectOrder}
              confirmLoading={confirmLoading}
              rejectLoading={rejectLoading}
              history={history}
              from={from}
            />
          </When>
          <Otherwise>
            <AcceptModal
              t={t}
              id={bookingId}
              confirmOrder={mutationConfirmOrder}
              type="list"
              record={record}
              confirmLoading={confirmLoading}
              history={history}
            />
            <div className={classNames(classes.divider, 'action-divider')} />

            <If condition={isHerculesBooking && record?.counterOffers?.length === 0}>
              <button
                className={classNames(classes.button, 'counterBtn')}
                onClick={() => {
                  handleClickCounterEventTrack(record);
                }}
              >
                {t('landlord.long_tail.booking.action.counter')}
              </button>
              <div className={classNames(classes.divider, 'action-divider')} />
            </If>

            <RejectModal
              t={t}
              id={bookingId}
              record={record}
              rejectLoading={rejectLoading}
              openOrderCounterOfferModal={openOrderCounterOfferModal}
              history={history}
              rejectOrder={(id, rejectedReason, cb) => {
                mutationRejectOrder({
                  variables: {
                    input: {
                      id,
                      rejectedReason,
                    },
                  },
                });
                cb();
              }}
              type="list"
            />
          </Otherwise>
        </Choose>
      </If>
      <If condition={stage !== orderStage.WAITING_FOR_CONFIRMATION}>
        <span className={classes.emptyAction}>-</span>
      </If>
    </Row>
  );
};

OrderActionField.propTypes = {
  stage: PropTypes.string,
  bookingId: PropTypes.string,
  refetchData: PropTypes.func,
  record: PropTypes.object,
  isHerculesBooking: PropTypes.bool,
  openOrderCounterOfferModal: PropTypes.func,
};

const BookingActionField = ({ id, record }) => {
  const { t } = useTranslation();
  const classes = useActionFieldStyles();
  const history = useHistory();

  const openReconcileDetailsPage = e => {
    e.stopPropagation();
    history.push({
      pathname: generatePath('reconciliation.detail', {
        id,
        bookingJourney: record?.opportunity?.bookingJourney,
      }),
      state: {
        record,
        bookingJourney: record?.opportunity?.bookingJourney,
      },
    });
  };

  return (
    <button
      className={classNames(classes.button, classes.buttonReconcile)}
      onClick={openReconcileDetailsPage}
    >
      {t('landlord.long_tail.booking.action.reconcile')}
    </button>
  );
};

BookingActionField.propTypes = {
  stage: PropTypes.string,
  id: PropTypes.string,
  record: PropTypes.object,
};

const BookingTable = ({
  tableData,
  loading,
  isHerculesBooking,
  isLeadBooking,
  isInstantBooking,
  otherTableFilter = {},
  setOtherTableFilter,
  refetchData,
  bookingJourney,
}) => {
  const { t } = useTranslation();
  const classes = useActionFieldStyles();
  const counterOfferModalRef = useRef(null);
  const isShowCounterOffer = useCallback(
    order => {
      //   order is counter offered，within the validity period
      const isPendingCounterOffer =
        order.secondaryStage === orderSecondaryStage.PENDING_COUNTER_OFFER;

      // TODO student accept/reject
      //  order is counter offered && without the validity period
      const isOverTimeCounterOffer =
        order.secondaryStage === orderSecondaryStage.COUNTER_OFFER_NON_CONFIRM_IN_TIME;
      //If the order is counter offered，landlord reject counter offer&original order
      const isRejectAll =
        order.secondaryStage === orderSecondaryStage.REJECT_BY_LANDLORD &&
        order.stage === orderStage.LOST &&
        !!order?.counterOffers?.length;
      return isPendingCounterOffer || isOverTimeCounterOffer || isRejectAll;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isHerculesBooking, isInstantBooking, isLeadBooking],
  );

  const isHerculesOrInstant = useMemo(
    () => isHerculesBooking || isInstantBooking,
    [isHerculesBooking, isInstantBooking],
  );

  const orders = useMemo(
    () =>
      (
        tableData?.viewer?.company?.[isHerculesOrInstant || isLeadBooking ? 'orders' : 'bookings']
          ?.edges ?? []
      ).map(item => item?.node),
    [isHerculesOrInstant, isLeadBooking, tableData?.viewer?.company],
  );

  const totalCount = useMemo(
    () =>
      tableData?.viewer?.company?.[isHerculesOrInstant || isLeadBooking ? 'orders' : 'bookings']
        ?.totalCount ?? 0,
    [isHerculesOrInstant, isLeadBooking, tableData?.viewer?.company],
  );

  const formatTableRoom = useCallback(
    (_, row) => {
      const record = isShowCounterOffer(row) ? row.counterOffers?.[0] : row;

      const unitTypeCategory =
        isHerculesOrInstant || isLeadBooking
          ? record?.unitTypeCategory
            ? record?.unitTypeCategory
            : row?.unitTypeCategory
          : record?.roomType;
      const unitTypeName =
        isHerculesOrInstant || isLeadBooking
          ? record?.unitTypeName
            ? record?.unitTypeName
            : row.unitTypeName
          : record?.roomName;

      return (
        <>
          <div>{record?.propertyName ?? row?.propertyName}</div>
          <div>
            {t(`landlord.long_tail.booking.unit_type_category.${unitTypeCategory?.toLowerCase()}`)}
            &nbsp;|&nbsp;{unitTypeName}
          </div>
        </>
      );
    },
    [isHerculesOrInstant, isLeadBooking, t, isShowCounterOffer],
  );

  const openOrderCounterOfferModal = row => {
    counterOfferModalRef.current?.handleOpenModal(row);
  };

  const actionField = useCallback(
    (text, record) => {
      if (isHerculesOrInstant) {
        return (
          <OrderActionField
            stage={record.stage}
            bookingId={record.id}
            refetchData={refetchData}
            record={record}
            isHerculesBooking={isHerculesBooking}
            openOrderCounterOfferModal={openOrderCounterOfferModal}
            from={pageType.bookingListPage}
          />
        );
      }
      if (isLeadBooking) {
        return (
          <AddNotesActionField
            stage={record.stage}
            bookingId={record.id}
            refetchData={refetchData}
            record={record}
            isHerculesBooking={isHerculesBooking}
            openOrderCounterOfferModal={openOrderCounterOfferModal}
          />
        );
      }
      return <BookingActionField id={record?.opportunity?.id} record={record} />;
    },
    [isHerculesBooking, isHerculesOrInstant, isLeadBooking, refetchData],
  );

  const columns = useMemo(
    () => {
      const columnList = [
        {
          title: t('landlord.long_tail.booking.booking_number.field'),
          dataIndex: 'id',
          key: 'id',
          width: '12%',
          className: classes.searchColumn,
          render: (text, record) => record.referenceId ?? '-',
          filterIcon: (
            <SearchOutlined
              style={{ color: otherTableFilter?.referenceId ? colors.color_keppel : undefined }}
            />
          ),
          filterDropdown: ({ confirm }) => (
            <TableSearchInput
              value={otherTableFilter.referenceId}
              onClick={value => {
                confirm();
                setOtherTableFilter({
                  ...otherTableFilter,
                  referenceId: value,
                  pageNumber: 1,
                });
              }}
            />
          ),
        },
        {
          title: t('landlord.long_tail.booking.student_name.field'),
          dataIndex: 'studentName',
          key: 'studentName',
          width: '14%',
          render: (_text, record) => {
            return getStudentName(record, bookingJourney);
          },
          filterIcon: (
            <SearchOutlined
              style={{
                color: otherTableFilter?.passportFullName ? colors.color_keppel : undefined,
              }}
            />
          ),
          filterDropdown: ({ confirm }) => (
            <TableSearchInput
              value={otherTableFilter.passportFullName}
              onClick={value => {
                confirm();
                setOtherTableFilter({
                  ...otherTableFilter,
                  passportFullName: value,
                  pageNumber: 1,
                });
              }}
            />
          ),
        },
        {
          title: t('landlord.long_tail.booking.room.field'),
          dataIndex: 'room',
          key: 'room',
          width: '18%',
          render: formatTableRoom,
        },
        {
          title: t('landlord.long_tail.booking.move_in_date.field'),
          dataIndex: 'moveInDate',
          key: 'moveInDate',
          width: '11%',
          className: classes.dateColumn,
          render: (text, row) => {
            let moveInDate = isShowCounterOffer(row) ? row.counterOffers?.[0]?.moveInDate : text;

            return moveInDate ? formatDay(dayFormatType.CONTENTDATE, moveInDate) : '-';
          },
          sorter: true,
        },
        {
          title: t('landlord.long_tail.booking.move_out_date.field'),
          dataIndex: 'moveOutDate',
          key: 'moveOutDate',
          width: '11%',
          className: classes.dateColumn,
          render: (text, row) => {
            let moveOutDate = isShowCounterOffer(row) ? row.counterOffers?.[0]?.moveOutDate : text;

            return moveOutDate ? formatDay(dayFormatType.CONTENTDATE, moveOutDate) : '-';
          },
        },
        {
          title: t('landlord.payment.long_tail.action.field'),
          key: 'action',
          fixed: 'right',
          className: isLeadBooking
            ? ''
            : isHerculesOrInstant
            ? classes.action
            : classes.actionReconcile,
          width: isHerculesOrInstant ? 198 : 170,
          render: actionField,
        },
      ];

      if (!isLeadBooking) {
        columnList.splice(5, 0, {
          title: t('landlord.long_tail.booking.total_price.field'),
          dataIndex: 'totalPrice',
          key: 'totalPrice',
          width: '10%',
          render: (text, row) => {
            const record = isShowCounterOffer(row) ? row.counterOffers?.[0] : row;

            return formatPrice(
              isHerculesOrInstant ? record?.totalPrice : record?.price,
              record?.currency,
            );
          },
        });
      } else {
        columnList.splice(5, 0, {
          title: 'Tenancy Length',
          key: 'Tenancy Length',
          width: '15%',
          render: (record, row) => {
            return (
              <div>{formatTenancyLength(row?.tenancyLength.weeks, row?.tenancyLength.days)}</div>
            );
          },
        });
        columnList.splice(6, 0, {
          title: 'Create Date',
          key: 'createdAt',
          width: '20%',
          render: (_, row) => {
            return <div>{row?.createdAt ? formatDay(dayFormatType.UTC, row?.createdAt) : '-'}</div>;
          },
        });
      }

      if (isHerculesOrInstant) {
        columnList.splice(6, 0, {
          title: t('landlord.long_tail.booking.booking_status.field'),
          dataIndex: 'stage',
          key: 'stage',
          width: '13%',
          render: text => t(`landlord.long_tail.booking.booking_status.${text.toLowerCase()}`),
        });
      } else if (!isLeadBooking) {
        columnList.splice(6, 0, {
          title: t('landlord.long_tail.booking.booking_status.field'),
          dataIndex: 'opportunity',
          key: 'opportunity',
          width: '13%',
          render: opportunity =>
            t(`landlord.long_tail.booking.booking_status.${opportunity?.stage?.toLowerCase()}`),
        });
      }

      if (isLeadBooking) {
        columnList.splice(5, 0, {
          title: t('cms:cms.property.commission.trigger_point'),
          key: 'commissionTriggerPoint',
          width: '20%',
          filters: getTriggerPointOptions(t),
          filterMultiple: false,
          render: (_, record) => (
            <span className="transfer__table-field">
              {record?.commissionTriggerPoint
                ? startCase(
                    getTriggerPointOptions(t).find(
                      item => item.value === record?.commissionTriggerPoint,
                    )?.text,
                  )
                : '-'}
            </span>
          ),
        });
      }

      return columnList;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actionField,
      classes.action,
      formatTableRoom,
      isHerculesOrInstant,
      isLeadBooking,
      otherTableFilter,
      setOtherTableFilter,
      t,
    ],
  );

  const handleTableChange = (pagination, filters, sorter) => {
    const oldDirection = otherTableFilter.sortDirection;
    const newDirection = sortDirection[sorter?.order];
    const oldPage = otherTableFilter.pageNumber;
    const newPage = pagination.current;

    if (oldDirection !== newDirection && oldPage === newPage) {
      if (!sorter?.order) {
        setOtherTableFilter({
          ...otherTableFilter,
          pageNumber: 1,
          sortBy: sortFields.createdAt,
          sortDirection: sortDirection.descend,
        });
      } else {
        setOtherTableFilter({
          ...otherTableFilter,
          pageNumber: 1,
          sortBy: sortFields[sorter.field],
          sortDirection: sortDirection[sorter.order],
        });
      }
    }

    if (filters.commissionTriggerPoint) {
      setOtherTableFilter({
        ...otherTableFilter,
        commissionTriggerPoint: convertEmptyArrayToNull(filters.commissionTriggerPoint, true),
      });
    }
  };

  const handlePageNumberChange = pageNumber => {
    setOtherTableFilter({
      ...otherTableFilter,
      pageNumber,
    });
  };

  const handlePageSizeChange = (_, pageSize) => {
    setOtherTableFilter({
      ...otherTableFilter,
      pageNumber: 1,
      pageSize,
    });
  };
  const openOrderDetailsPage = record => {
    window.open(
      generatePath(
        isLeadBooking ? 'orders.lead_details' : 'orders.detail',
        {
          id: record?.referenceId,
        },
        { from: 'Booking List' },
      ),
      '_blank',
    );
  };
  return (
    <div>
      <Table
        rowKey="id"
        scroll={{ x: 1346 }}
        columns={columns}
        dataSource={orders}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: otherTableFilter?.pageNumber,
          pageSize: otherTableFilter?.pageSize,
          showSizeChanger: true,
          hideOnSinglePage: false,
          total: totalCount,
          onChange: handlePageNumberChange,
          onShowSizeChange: handlePageSizeChange,
        }}
        onRow={record => {
          if (record?.opportunity?.bookingJourney === 'MANUAL') return;
          return {
            onClick: () => {
              event('Clicked Action Btn in Booking List Page', {
                'Clicked Position': 'Booking Detail Card',
                'Current Page Type': pageType.bookingListPage,
              });

              openOrderDetailsPage(record);
            },
          };
        }}
      />

      <CounterOfferModal
        wrappedComponentRef={counterOfferModalRef}
        refetchBookingList={refetchData}
        from={pageType.bookingListPage}
      />
    </div>
  );
};

BookingTable.propTypes = {
  tableData: PropTypes.object,
  loading: PropTypes.bool,
  isHerculesBooking: PropTypes.bool,
  isInstantBooking: PropTypes.bool,
  otherTableFilter: PropTypes.object,
  setOtherTableFilter: PropTypes.func,
  refetchData: PropTypes.func,
};

export default BookingTable;
